import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X, PaperPlaneTilt, ChatCircle, Spinner, ArrowClockwise } from "@phosphor-icons/react";
import { sendSms, fetchSmsThread, refreshSmsStatus, twilioHealth } from "@/lib/api";

/**
 * SmsChatPanel
 * --------------
 * Side-drawer SMS thread for a customer. Loads existing inbound + outbound
 * messages from /api/twilio/sms/threads/{customer_id} and lets an admin
 * send a new outbound SMS to a specific PFCID person.
 */
export default function SmsChatPanel({
  open,
  onClose,
  customerId,
  customerName = "",
  personId,
  personName = "",
  personPhone = "",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [smsFrom, setSmsFrom] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const scrollRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!open || !customerId) return;
    setLoading(true);
    try {
      // Ask Twilio for the latest delivery status of any pending messages,
      // then reload the thread so the bubbles reflect 'sent' / 'delivered'
      // / 'failed' instead of the create-time 'queued' value.
      try { await refreshSmsStatus(customerId); } catch { /* non-fatal */ }
      const { items: rows } = await fetchSmsThread(customerId);
      setItems(rows || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load SMS thread");
    } finally {
      setLoading(false);
    }
  }, [open, customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Pull the live Twilio sender number once when the panel opens so the
  // "Outbound from …" hint always matches whatever's actually configured
  // (the .env can change when the user buys/swaps numbers).
  useEffect(() => {
    if (!open) return;
    twilioHealth().then((h) => setSmsFrom(h?.sms_from || "")).catch(() => {});
  }, [open]);

  // F2 — AI smart-reply suggestions. Refresh whenever the thread changes
  // and there's at least one message. Cheap (~0.5s Claude call), debounced
  // so we don't spam on every status-poll tick.
  const fetchSuggestions = useCallback(async () => {
    if (!open || !customerId || items.length === 0) {
      setSuggestions([]);
      return;
    }
    setSuggesting(true);
    try {
      const params = personId ? { person_id: personId } : {};
      const r = await api.get(`/twilio/sms/threads/${encodeURIComponent(customerId)}/suggestions`, { params });
      setSuggestions(r.data?.suggestions || []);
    } catch {
      setSuggestions([]);
    } finally {
      setSuggesting(false);
    }
  }, [open, customerId, personId, items.length]);
  useEffect(() => {
    // Only refresh when the LAST message changes (keeps cost bounded)
    if (!open) return;
    const lastId = items[items.length - 1]?.id;
    fetchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId, items[items.length - 1]?.id]);

  // Auto-poll Twilio every 5s while the panel is open AND there's at least
  // one outbound message that hasn't reached a terminal state. This avoids
  // the "queued forever" UX while Twilio takes 5-30s to hand off to a carrier
  // (especially for international destinations).
  useEffect(() => {
    if (!open) return undefined;
    const TERMINAL = new Set(["delivered", "failed", "undelivered", "received", "read"]);
    const hasPending = items.some(m => m.direction === "outbound" && !TERMINAL.has((m.status || "").toLowerCase()));
    if (!hasPending) return undefined;
    const id = setInterval(() => { refresh(); }, 5000);
    return () => clearInterval(id);
  }, [open, items, refresh]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [items]);

  const onSend = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true); setError(null);
    try {
      await sendSms({ customer_id: customerId, person_id: personId, body: text });
      setBody("");
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[300] flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl flex flex-col" data-testid="sms-chat-panel">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-2">
            <ChatCircle size={18} weight="fill" className="text-blue-600" />
            <div>
              <div className="text-sm font-semibold text-slate-900">SMS · {customerName}</div>
              <div className="text-[11px] text-slate-500">
                {personName ? `→ ${personName}` : ""} {personPhone ? `· ${personPhone}` : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={refresh} data-testid="sms-refresh-btn"
              disabled={loading}
              className={`p-1 ${loading ? "text-blue-600" : "text-slate-500 hover:text-slate-800"}`}
              title={loading ? "Refreshing…" : "Refresh"}>
              <ArrowClockwise size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={onClose} data-testid="sms-close-btn" className="p-1 text-slate-500 hover:text-slate-800">
              <X size={18} />
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
          {!loading && items.length === 0 && (
            <div className="text-center text-xs text-slate-500 py-8">
              No SMS messages yet. Send the first one below — replies will arrive here automatically.
            </div>
          )}
          {items.map(m => {
            const out = m.direction === "outbound";
            const failed = ["failed", "undelivered"].includes((m.status || "").toLowerCase());
            return (
              <div key={m.id} className={`flex ${out ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                  out ? (failed ? "bg-rose-600 text-white" : "bg-blue-600 text-white") : "bg-white border border-slate-200 text-slate-900"
                }`} data-testid={`sms-bubble-${m.id}`}>
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                  <div className={`mt-1 text-[10px] ${out ? (failed ? "text-rose-100" : "text-blue-100") : "text-slate-400"}`}>
                    {m.person_name ? `${m.person_name} · ` : ""}
                    {new Date(m.created_at).toLocaleString()}
                    {out && m.status ? ` · ${m.status}` : ""}
                  </div>
                  {failed && m.twilio_error_message && (
                    <div className="mt-1 text-[10px] text-rose-100 italic" data-testid={`sms-bubble-error-${m.id}`}>
                      Twilio error {m.twilio_error_code}: {m.twilio_error_message}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-slate-200 p-3 bg-white">
          {error && <div className="mb-2 text-xs text-rose-600">{error}</div>}
          {/* AI smart-reply suggestions (F2) */}
          {(suggesting || suggestions.length > 0) && (
            <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="sms-suggestions">
              <span className="text-[9px] font-bold uppercase tracking-wider text-violet-500 inline-flex items-center gap-1">
                {suggesting ? <Spinner size={9} className="animate-spin" /> : "✨"}
                AI suggests
              </span>
              {suggestions.map((s, i) => (
                <button key={i}
                  type="button"
                  data-testid={`sms-suggestion-${i}`}
                  onClick={() => { setBody(s); }}
                  className="text-[11px] text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-full px-2.5 py-1 transition-colors max-w-full truncate"
                  title={s}>
                  {s}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              data-testid="sms-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={personPhone ? `Message ${personName || personPhone}…` : "Type a message…"}
              rows={2}
              className="flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onSend(); }}
            />
            <button onClick={onSend} disabled={sending || !body.trim()}
              data-testid="sms-send-btn"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-blue-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-700 disabled:opacity-50">
              {sending ? <Spinner size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="fill" />}
              Send
            </button>
          </div>
          <div className="mt-1 text-[10px] text-slate-400">
            {smsFrom ? <>Outbound from <span className="font-mono">{smsFrom}</span>.</> : "Outbound number not configured."}{" "}
            Replies route back to this thread automatically.
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
